import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from "react-native-reanimated";
import React, { ReactElement, useLayoutEffect, useMemo } from "react";
import { ReordrableProps } from "./ReordrableListItem";

type Positions = Record<string, number>;

type ReorderableListProps<T> = {
  items: readonly T[];
  itemHeight: number;
  renderItem(
    item: T,
    reorderableListItemProps: ReordrableProps,
    index: number,
    arr: readonly T[]
  ): ReactElement;
  keyExtractor(item: T): string;
  onReorder(order: Positions): void;
  disabled?: boolean;
  hasFooter?: boolean;
  iconPosition?: "left" | "right";
};

export function ReorderableList<T>(props: ReorderableListProps<T>) {
  const activeItem = useSharedValue<string | null>(null);

  const p = Object.fromEntries(
    props.items.map((item, index) => [props.keyExtractor(item), index])
  );

  const positions = useSharedValue<Positions>(p);
  const itemsKey = useMemo(
    () => props.items.map(props.keyExtractor).join(""),
    [props.items, props.keyExtractor]
  );
  useLayoutEffect(() => {
    positions.value = p;
  }, [itemsKey, p, positions, props, props.keyExtractor]);

  const height = useDerivedValue(() => {
    return Object.keys(positions.value).length * props.itemHeight;
  });
  const listStyle = useAnimatedStyle(() => ({
    height: height.value,
    width: "100%",
  }));
  return (
    <Animated.View style={listStyle}>
      {props.items.map((item, i, arr) =>
        props.renderItem(
          item,
          {
            positions,
            id: props.keyExtractor(item),
            height: props.itemHeight,
            onReorder: props.onReorder,
            activeItem: activeItem,
            disabled: props.disabled,
            hasFooter: props.hasFooter,
            iconPosition: props.iconPosition,
            listHeight: height,
          },
          i,
          arr
        )
      )}
    </Animated.View>
  );
}
